/**
 * Status pip selection (TRD-03 R-03-16).
 *
 * Seven statuses are simulated. Four pips fit above a health bar at 16 px per tile before
 * they stop being readable and start being a smear, so this module owns the two decisions
 * that follow from that: which four, and in what colour.
 *
 * Kept separate from `renderer.ts` and free of Pixi so the priority and the cap can be
 * unit-tested. The renderer's own logic cannot be — it needs a WebGL context — and "which
 * status did the player actually get to see" is exactly the kind of rule that is worth
 * pinning down in a test rather than eyeballing in a crowded frame.
 */
import { INACTIVE, type StatusKind, type StatusState } from '@prism/sim';

/**
 * Priority order, most important first. Bit N of a status mask is `ORDER[N]`.
 *
 * Hard crowd control leads. `freeze` and `stun` are the only two statuses that change what
 * an enemy *does* rather than how fast it dies, so an enemy standing still with no pip
 * explaining why is the one genuinely confusing frame — worse than a hidden poison stack,
 * which the falling health bar already tells the story of.
 *
 * After those, damage-over-time before slows before amplifiers: the ordering a player
 * reads when deciding whether a group is already handled.
 */
export const STATUS_PIP_ORDER = [
  'freeze',
  'stun',
  'poison',
  'chill',
  'shock',
  'wither',
  'mark',
] as const satisfies readonly StatusKind[];

/**
 * Colour-matched to the class that applies each status (R-03-16), taken from the class
 * palette in `@prism/content` rather than invented here — Venom's acid, Frost's cyan,
 * Tempest's white-blue, Void's magenta, Arcanist's violet. They are duplicated as literals
 * because the renderer never imports content (see `TowerVisualLookup`), and V-11 already
 * guarantees the source colours sit ≥ 25 ΔE apart, which is what makes them work at 3 px.
 *
 * `stun` and `freeze` are not in R-03-16's list, because R-03-16 does not show them at all.
 * They get colours outside the class palette on purpose: hard CC is not a class identity,
 * it is a state, and it should not read as "a Tempest did this".
 */
export const STATUS_PIP_TINT: Record<StatusKind, number> = {
  poison: 0x8ac926, // venom
  chill: 0x4cc9f0, // frost
  shock: 0xdbe9ff, // tempest
  wither: 0xff006e, // void
  mark: 0x9b5de5, // arcanist
  stun: 0xffb703, // amber — stopped
  freeze: 0xffffff, // the hardest CC gets the brightest pip
};

/** R-03-16. Four is the readability limit above a health bar, not an arbitrary cap. */
export const MAX_STATUS_PIPS = 4;

/** Pip size and spacing in logical pixels. */
export const PIP_PX = 3;
export const PIP_GAP = 1;

/**
 * A bitmask of the active statuses, bit N being `STATUS_PIP_ORDER[N]`.
 *
 * `expiresAtTick` alone is the test: `tickStatuses` clears an expired status to `INACTIVE`
 * in place every tick (TRD-05 §7), so world state is exact and no tick comparison is
 * needed. Reading the expiry against the current tick instead would show a pip for one
 * extra frame at 4× speed, on the tick between expiry and the sweep.
 */
export function statusMaskOf(s: StatusState): number {
  let mask = 0;
  if (s.freeze.expiresAtTick !== INACTIVE) mask |= 1;
  if (s.stun.expiresAtTick !== INACTIVE) mask |= 2;
  if (s.poison.expiresAtTick !== INACTIVE) mask |= 4;
  if (s.chill.expiresAtTick !== INACTIVE) mask |= 8;
  if (s.shock.expiresAtTick !== INACTIVE) mask |= 16;
  if (s.wither.expiresAtTick !== INACTIVE) mask |= 32;
  if (s.mark.expiresAtTick !== INACTIVE) mask |= 64;
  return mask;
}

/**
 * Fill `out` with up to `MAX_STATUS_PIPS` tints for a mask, in priority order.
 *
 * Returns how many were written. `out` is caller-owned and reused, so a crowded wave costs
 * no allocation for something that is recomputed whenever a status lands or expires.
 */
export function pipTints(mask: number, out: number[]): number {
  let n = 0;
  for (let i = 0; i < STATUS_PIP_ORDER.length && n < MAX_STATUS_PIPS; i++) {
    if ((mask & (1 << i)) === 0) continue;
    out[n++] = STATUS_PIP_TINT[STATUS_PIP_ORDER[i] as StatusKind];
  }
  return n;
}
